import { AnimatedSprite, Sprite, Ticker } from "pixi.js"
import { predictedFallAirborne, predictedFallPending } from "../../../../predicted_fall"
import { isAirborne } from "../effects/floor_fall"
import { Entity } from "game-common/models"
import { LpcJobResult } from "./lpc_composite_generator"
import { AnimationDictionaryType, HeldPose, LpcCompositeSprite, LpcSpriteRenderMeta } from "./lpc_composite_sprite"
import { BakedEmberAnimation, emberFrameBaker } from "./ember_frame_baker"
import {
    EmberEquipment,
    EmberJoints,
    AGAIN,
    MOVING_ATTACK_GAITS,
    SHORT_STRIDE,
    ParsedAnimation,
    TOWARD,
    aimedShotName,
    aimsShots,
    movingAttackName,
    parseAnimationName,
    planEmberAnimation,
    heldFrame,
} from "game-common/ember_sprite/generate"
import { BOW_DOWN_ACTION, BOW_READY_ACTION, SHORT_STRIDES, IDLE_VARIATIONS, isIdleVariation, THREATEN_ACTIONS, CLIMB_RISE, EMBER_CLIPS, LPC_ACTION_DURATION_MS } from "game-common/ember_sprite/clips"
import { FROM_REST, LEAP_PHASES, SETTLE, SHIFT, STANCE_ATTACKS, START } from "game-common/ember_sprite/settle"
import { BodyAnimationPose } from "../effects/animation_pose"
import { ladderRiseOf } from "../effects/stair_travel"
import { heldObjectTexture } from "./lpc_held_object"
import { ONSET_SHARE, WalkAnimationPace } from "./walk_animation_pace"

/** How big a carried load is drawn: its longer side, in pixels (LPC draws its held objects at 18). */
export const EMBER_LOAD_SIZE = 16
import { FACINGS, Facing, emberFacingAt, facingScreenAngle, isBackFacing } from "game-common/ember_sprite/raster"
import { EMBER_HUMANOIDS } from "game-common/ember_sprite/humanoids"
import { JUMP_FLOURISH } from "game-common/animation_pose"
import { JUMP_HOLD, JUMP_LANDING, JUMP_MS } from "game-common/jump"

/**
 * For a body with no run key (an NPC): above this measured ground speed (px/s) it runs; it walks
 * again below the lower one. A player's gait is the run key instead (see EmberCompositeSprite.runKey).
 */
export const RUN_ABOVE = 78
export const WALK_BELOW = 66

/** The LPC body frame every Ember frame centres its body in. */
const BODY_FRAME = 64

/**
 * A walk start this new is swapped for the run's when the run key turns out to be held: the
 * server's word on it (Entity.boosted) comes a moment after the first step.
 */
export const START_SWAP_MS = 200

/**
 * How long a turning body shows each step it turns through on the way round: a facing, or (for a
 * player's body) the angle half way between two (generate.ts `<loop>.toward.<facing>-<facing>`).
 */
export const TURN_STEP_MS = 30

/** The facings round the screen, clockwise from screen right. */
const SCREEN_RING: Facing[] = ["right", "frontright", "front", "frontleft", "left", "backleft", "back", "backright"]

/** Where a drawing stands on the ring of 16 a body turns round: a facing's twice its place, half way between odd. */
const ringPlace = (parsed: ParsedAnimation): number => {
    const a = SCREEN_RING.indexOf(parsed.facing) * 2
    if (!parsed.toward) return a
    const b = SCREEN_RING.indexOf(parsed.toward) * 2
    return ((a + b + (Math.abs(a - b) > 2 ? 16 : 0)) / 2) % 16
}

/** The place on the ring of 16 nearest an aim (radians, clockwise from screen right): emberFacingAt's, twice as fine. */
const aimPlace = (angle: number): number => ((Math.round(angle / (Math.PI / 8)) % 16) + 16) % 16

/** The drawing of `loop` at a place on the ring of 16. */
const ringDrawing = (loop: string, place: number): AnimationDictionaryType =>
    (place % 2 === 0
        ? `${loop}-${SCREEN_RING[place / 2]}`
        : `${loop}.${TOWARD}.${SCREEN_RING[((place + 1) / 2) % 8]}-${SCREEN_RING[(place - 1) / 2]}`) as AnimationDictionaryType

/**
 * `size` places round from `from` toward `to` on the ring of 16, or `to` itself when it is no
 * further. Turning right about, the body comes round by the side that faces the camera.
 */
export const ringToward = (from: number, to: number, size: number): number => {
    let steps = ((((to - from) % 16) + 24) % 16) - 8
    if (steps === -8 && [1, 2, 3, 4, 5, 6, 7].some(k => (from + k) % 16 === 4)) steps = 8
    return Math.abs(steps) <= size ? to : (from + Math.sign(steps) * size + 16) % 16
}

/** A plain loop -- a stride or standing -- whose frames go on from one facing (or gait) to the next. */
const loopsOn = (parsed: ParsedAnimation | undefined): boolean =>
    !!parsed && !parsed.gait && (MOVING_ATTACK_GAITS.includes(parsed.action) || parsed.action === "idle")

/**
 * The longest a start may take at the pace the body is going. A start is mostly a weight shift before
 * its first step lifts, played at the stride's own cadence: a slow body (an NPC creeping at 40px/s)
 * would spend most of a second over it, sliding before the legs step. Slower than this, it walks off.
 */
export const START_MAX_MS = 450

/**
 * Walking backwards -- an enemy backing off while it faces its foe, a player walking away from where
 * it aims -- the stride plays in reverse: the heel-to-toe roll run backwards is the toe-to-heel step of
 * someone walking backwards, and its planted foot then travels with the ground. Backwards when the way
 * the body is drawn going is more than BACKWARD_ON off the way it faces; forwards again inside
 * BACKWARD_OFF (cosines of the angle between). Below TRAVEL_MIN px/s of drawn motion, unchanged.
 */
const BACKWARD_ON = -0.5
const BACKWARD_OFF = -0.3
const TRAVEL_MIN = 6
/** How quickly the drawn heading follows the body's moves (share per move), so a nudge does not swing it. */
const TRAVEL_SMOOTHING = 0.3

/**
 * Below these drawn speeds (px/s) a walk takes the shorter strides of SHORT_STRIDES, in turn: a body
 * going slowly takes smaller steps, not the full stride in slow motion. STRIDE_MARGIN either side of
 * each keeps a body hovering at one from flickering between strides.
 */
const SHORT_STRIDE_BELOW = [55, 38, 24]
const STRIDE_MARGIN = 4

/**
 * How long an NPC that has just stopped holds its stride's frame before it settles: a brain stops
 * and goes again often, and settling at every pause (then setting off afresh) churns instead of walking.
 */
export const NPC_STOP_HOLD_MS = 180

/** How long a stride carries on toward a heel strike to change gait there before it just changes. */
export const SHIFT_WAIT_MS = 600

/** The stride a start or a gait change hands over to: a start's own, a change's other one. */
const strideAfter = (parsed: ParsedAnimation): string =>
    parsed.action === START || parsed.action === BOW_DOWN_ACTION ? parsed.after! : isLeap(parsed) ? parsed.gait! : MOVING_ATTACK_GAITS.find(gait => gait !== parsed.after)!
/** A jump on the move (generate.ts `jump.<gait>.<quarter>`): a leap out of the stride and back into it. */
const isLeap = (parsed: ParsedAnimation | undefined): boolean => parsed?.action === "jump" && !!parsed.gait
/** A start, gait change or leap: played when wanted, handing back to a stride where it leaves the legs. */
const handsOver = (parsed: ParsedAnimation | undefined): boolean =>
    parsed?.action === START || parsed?.action === SHIFT || isLeap(parsed) || (parsed?.action === BOW_DOWN_ACTION && !!parsed.after)

/** How soon after a leap comes down the stride must pick up to go on from it, rather than set off afresh. */
const LEAP_LANDING_MS = 250

/** How long an archer standing keeps the bow up after a shot, ready for the next, before lowering it. */
export const BOW_READY_MS = 1125

/** How long a stopped stride holds its frame for its settle to bake before it gives up and stands. */
export const SETTLE_WAIT_MS = 300

/** PIXI plays `animationSpeed` frames per 60Hz tick. */
const TICKS_PER_SECOND = 60

/** How often a standing humanoid chooses whether to play an idle gesture, and how likely it is to. */
const IDLE_CHOICE_MS = 2000
const IDLE_GESTURE_CHANCE = 0.3

/**
 * Standing, the body breathes (clips.ts sampleIdle) but now and then stands quite still instead: at
 * each rest pose between breaths it holds that frame, this likely, for between these long. A breath
 * takes 3s, so the body is still about 30% of the time.
 */
const IDLE_HOLD_CHANCE = 0.43
const IDLE_HOLD_MS = [2000, 4000]

/**
 * Frames per tick that keep a gait's planted feet still on the ground: the clip covers
 * `strideCycle` pixels per cycle, so at `speed` px/s it must cycle speed / strideCycle times a second.
 */
export const gaitAnimationSpeed = (speed: number, strideCycle: number, frames: number): number =>
    (speed / strideCycle) * (frames / TICKS_PER_SECOND)

/**
 * A body drawn from a generated Ember humanoid instead of LPC sheets.
 *
 * Everything around the frames -- shadow, rings, halos, hover outline, held poses, attack
 * callbacks, the death shatter -- is LpcCompositeSprite's, unchanged. What differs is where an
 * action's frames come from (rendered from the reference skeleton, see game-common/ember_sprite),
 * that the body can RUN as well as walk -- a player while holding the run key (Shift), so the gait is
 * the same on every client -- and that a gait is timed from the ground it covers rather
 * than from a tuned multiplier: the clip knows its stride exactly, so the feet never slide.
 *
 * It also STRIKES ON THE MOVE: an attack started while walking or running plays the attack's
 * upper body over that gait's legs, from where the stride had got to, and hands back to the gait
 * where the strike left the legs -- instead of LPC's standing swing sliding across the ground.
 *
 * And a player's body COMES TO REST (game-common/ember_sprite/settle.ts) instead of cutting to its
 * standing frame: stopping, it settles from the frame its stride was on; a blow struck from standing
 * eases out of the standing pose and settles back into it afterwards. A settle bakes the first
 * time it is wanted, the stride holding its frame meanwhile. NPCs still cut: every look would bake
 * its own settles, and there are many more of them.
 */
export class EmberCompositeSprite extends LpcCompositeSprite {
    // No floor on the cadence (a generated gait's stride is exact, so it follows even a very slow
    // follower), except while setting off: see WalkAnimationPace's onset.
    protected walkPace = new WalkAnimationPace(0, ONSET_SHARE, true)
    readonly spriteName: string
    /** What the body holds, drawn into every frame; a change of equipment builds a new sprite. */
    readonly equipment: EmberEquipment
    private nextIdleChoice = Date.now() + IDLE_CHOICE_MS
    private pendingIdle?: AnimationDictionaryType
    /** Holding the idle still on its rest frame until then (see IDLE_HOLD_CHANCE). */
    private stillUntil?: number
    /** Whether to hold has been settled for the rest frame on show. */
    private restFrameDecided = false
    /** Of the undead: stands frozen in its rest pose, not breathing (as the undead bodies' own idle). */
    private breathless = false
    private running = false
    /**
     * A PLAYER runs while the server has them running (Entity.boosted: Shift held, see
     * Message.boosted) and walks otherwise, however fast they are going. Undefined for a body with
     * no run key, which chooses by its speed instead.
     */
    private runKey?: boolean
    private baked = new Map<string, BakedEmberAnimation>()
    /** Set as a strike on the move starts: the gait to hand back to, and the strike it follows. */
    private resumeGait?: { action: AnimationDictionaryType; after: AnimationDictionaryType }
    /** The screen heading the next shot flies along (aimShot), for a body that aims its shots. */
    private shotHeading?: number
    private readonly standingPostAnimation = this.postAnimation
    /**
     * Moves through transitions rather than cutting between loops -- comes to rest through settles,
     * sets off through starts, changes gait and turns by steps: any generated humanoid, player or NPC.
     * An NPC has each made the first time it wants it (every look bakes its own, and there are many).
     */
    private settles = false
    /** A player's body: it also jumps and leaps, faces its aim to the nearest 22.5 degrees standing, and has its transitions made ahead of time. */
    private player = false
    /**
     * The settle a stopped stride is waiting on to bake. The stride holds its frame meanwhile -- the
     * settle starts from that frame -- and picks up again if the body moves first.
     */
    private pendingSettle?: { name: AnimationDictionaryType; stride: AnimationDictionaryType; since: number }
    /** The facing a jump is drawn in, taken as it leaves the ground and kept until it is down. */
    private jumpFacing?: Facing
    /** The leap's drop the body is in (FloorFallView.leap), and when the last one landed. */
    private leapFallId?: string
    private leapLandedAt?: number
    /** Facings a player's body has had its jump drawn for ahead of time, so the first one is not late. */
    private readonly jumpsBaked = new Set<string>()
    /** A player's body has had its run starts drawn ahead of time, every facing (settle.ts startIntoStride). */
    private startsBaked = false
    /** This body's walk and run, every facing, have been queued to draw in idle moments. */
    private gaitsQueued = false
    /** The start or gait change under way: the stride it hands over to picks up where it left the legs. */
    private handingOver?: AnimationDictionaryType
    private setOffAt = 0
    /** Since when the body has wanted the other gait, carrying on to a heel strike to change there. */
    private shiftWanted?: number
    /** The jump under way: the standing jump, or a leap out of the stride the body was in. */
    private jumpAction?: AnimationDictionaryType
    /** A leap just come down: the stride goes on from where it left the legs (locomotionAnimation). */
    private landedLeap?: { leap: AnimationDictionaryType; at: number }
    /** Strides and facings a player's body has had its leaps made ready for. */
    private readonly leapsPrepared = new Set<string>()
    /** An NPC's stride held on its frame since it stopped (NPC_STOP_HOLD_MS), to go on if it moves again. */
    private strideHeld?: { action: AnimationDictionaryType; since: number }
    /** Which stride a slow walk takes: 0 the full one, else SHORT_STRIDES[tier - 1] (shortened). */
    private strideTier = 0
    /** The way the body is drawn going (screen px, smoothed over its moves) and when it last moved. */
    private travel = { x: 0, y: 0, at: 0 }
    /** Going backwards to the way it faces: its stride plays in reverse (applyPace). */
    private backward = false
    /** Set while a request is made ahead of time (prepareAhead), so the baker queues it behind what is wanted now. */
    private ahead = false
    /**
     * Have an animation made ready for later -- a start, a turn's step, a gait change, a leap -- baked
     * behind anything a body wants to show now, or a walk wanted now waits behind them and the body
     * glides along in its standing frame until it lands.
     */
    private prepareAhead = (action: AnimationDictionaryType) => {
        this.ahead = true
        try {
            this.prepare(action)
        } finally {
            this.ahead = false
        }
    }
    /** Strides and facings a player's body has had the half way turns either side made ready for. */
    private readonly turnsPrepared = new Set<string>()
    /** Since when the bow has been held up after the last shot (clips.ts bowStream): lowered after BOW_READY_MS. */
    private bowReadySince?: number
    /** Facings a player's body has had its gait changes made ready for. */
    private readonly shiftsPrepared = new Set<string>()
    /**
     * The loop on show as another took over (a stride, or standing): where it had got to, so a new
     * facing or gait goes on from there instead of from its first frame -- no jump in the legs.
     */
    private leaving?: { action: AnimationDictionaryType; phase: number }
    /** When the body last turned a step round toward the way it faces (turnToward). */
    private turnedAt = 0
    /** Where the body is aiming (Entity.movement.angle): a player standing faces it to the nearest 22.5 degrees. */
    private aim?: number
    /** The item carried in the arms (EmberEquipment.carry): its art, between the hands, frame by frame. */
    private readonly loadSpriteId?: string
    private load?: Sprite

    constructor(spriteName: string, meta: LpcSpriteRenderMeta, equipment: EmberEquipment = {}) {
        // No sheets to load, and no LPC held-object art baked in: it is placed against LPC hands. A
        // held weapon is generated into the frames instead (game-common/ember_sprite, EMBER_HELD); a
        // carried item is the item's own art, held between the Ember body's hands (updateLoad).
        // A generated humanoid breathes in its own idle frames (clips.ts sampleIdle): no stretch over them.
        super({ ...meta, descriptors: [], heldObject: undefined, breathingSeed: EMBER_HUMANOIDS[spriteName] ? undefined : meta.breathingSeed })
        this.spriteName = spriteName
        this.equipment = equipment
        this.loadSpriteId = equipment.carry ? meta.heldObject?.spriteId : undefined
        if (this.loadSpriteId) Ticker.shared.add(this.updateLoad)
        // Note where the loop on show had got to whenever another takes over (animationStartFrame).
        const activate = this.activateAnimation
        this.activateAnimation = (action, force) => {
            // Wanted now and not drawn yet: to the front of the baker's queue, even if it was asked
            // for ahead of time and is waiting at the back.
            if (!this.animations[action] && parseAnimationName(action)) emberFrameBaker.request(this.spriteName, action, true, () => {}, () => this.spriteReleased, this.equipment)
            const ready = parseAnimationName(action)
            if (ready?.action === BOW_READY_ACTION && action !== this.action) {
                this.bowReadySince = Date.now()
                // Setting off from the bow held up: have the starts out of it ready.
                if (this.settles) for (const gait of MOVING_ATTACK_GAITS) this.prepareAhead(`${START}.${gait}.${BOW_READY_ACTION}-${ready.facing}` as AnimationDictionaryType)
            }
            const legs = this.animations[this.action]?.animation
            const was = parseAnimationName(this.action)
            this.leaving = legs && action !== this.action && (loopsOn(was) || (was?.action === BOW_READY_ACTION && !!was.after))
                ? { action: this.action, phase: legs.currentFrame / legs.totalFrames }
                : undefined
            activate(action, force)
        }
    }

    /**
     * What to draw `loop` as now, facing `target`: reached a step at a time round the facings between
     * (TURN_STEP_MS each) -- for a player's body by the angles half way between them too -- when it is
     * more than a step from the drawing on show. A player STANDING faces its aim to the nearest of
     * those 16 angles, resting on a half way one when it aims there. A step whose frames are not ready
     * yet is made ready for next time and turned straight past.
     */
    private turnToward = (loop: string, target: string): AnimationDictionaryType => {
        const straight = `${loop}-${target}` as AnimationDictionaryType
        const shown = parseAnimationName(this.action)
        // Nothing on show yet (its first frames still baking): nothing to turn from, so straight there.
        if (!shown || !this.animations[this.action] || !EMBER_HUMANOIDS[this.spriteName] || !SCREEN_RING.includes(target as Facing)) return straight
        // An NPC on the move turns at once: stepping round, its stride is drawn the old way while it
        // already goes the new -- a reversal glides sideways. Standing (looking round), it steps.
        if (!this.player && loop !== "idle") return straight
        const size = this.settles ? 1 : 2
        const from = ringPlace(shown)
        let to = SCREEN_RING.indexOf(target as Facing) * 2
        if (loop === "idle" && this.player && this.settles && this.aim !== undefined) {
            // The aim's own angle, when it is beside the facing it is in (a facing changing under it
            // is still on its way) and that half way drawing is ready; made ready for next time if not.
            const aimed = aimPlace(this.aim)
            if (Math.abs(((aimed - to + 24) % 16) - 8) === 1) {
                if (this.animations[ringDrawing(loop, aimed)]) to = aimed
                else this.prepareAhead(ringDrawing(loop, aimed))
            }
        }
        const there = ringDrawing(loop, to)
        if (from === to) return there
        // Every step is held its while, the last one onto the facing too: a quick sweep (WASD swings the
        // aim through a sector in a few ms) would otherwise show each half way angle for a single frame.
        if (Date.now() - this.turnedAt < TURN_STEP_MS * size) return ringDrawing(loop, from)
        if (ringToward(from, to, size) === to) {
            this.turnedAt = Date.now()
            return there
        }
        // The next step round, or failing it (not drawn yet) the one after, or straight there.
        for (let place = ringToward(from, to, size); place !== to; place = ringToward(place, to, size)) {
            const step = ringDrawing(loop, place)
            if (this.animations[step]) {
                this.turnedAt = Date.now()
                return step
            }
            this.prepareAhead(step)
        }
        return there
    }
    /**
     * The carried item, kept between the hands of the frame on show (its baked joints) -- so it rises
     * and falls with the arms on the move -- in front of the body, or behind it facing away (where
     * LPC puts its held objects too), tinted as the body is. Shown only while the arms are holding
     * it: an attack, a cast or a climb puts it out of sight.
     */
    private updateLoad = () => {
        if (!this.loadSpriteId || !this.initialized || !this.container) return
        const job = this.animations[this.action]
        const animation = job?.animation
        const holding = !!this.baked.get(this.action)?.carrying && !!animation?.visible
        if (!this.load) {
            if (!holding) return
            const texture = heldObjectTexture(this.loadSpriteId)
            if (!texture) return
            this.load = new Sprite(texture)
            // Resting on the forearms: centred over the hands, most of it above them.
            this.load.anchor.set(0.5, 0.75)
            this.load.scale.set(EMBER_LOAD_SIZE / Math.max(texture.orig.width, texture.orig.height))
        }
        const load = this.load
        const hands = holding ? this.jointLocal(j => ({ x: (j.hands[0].x + j.hands[1].x) / 2, y: (j.hands[0].y + j.hands[1].y) / 2 })) : undefined
        load.visible = !!hands
        if (!hands || !animation) return
        load.position.set(hands.x, hands.y)
        load.tint = animation.tint
        const behind = isBackFacing(parseAnimationName(this.action)?.facing ?? this.facingDir)
        const container = this.container
        const at = load.parent === container ? container.getChildIndex(load) : -1
        const body = container.getChildIndex(animation)
        if (at < 0 || (behind ? at !== body - 1 : at !== body + 1)) {
            if (at >= 0) container.removeChild(load)
            const index = container.getChildIndex(animation)
            container.addChildAt(load, behind ? index : index + 1)
        }
    }

    remove() {
        Ticker.shared.remove(this.updateLoad)
        super.remove()
    }

    destroy() {
        Ticker.shared.remove(this.updateLoad)
        super.destroy()
    }

    protected requestAnimation(action: AnimationDictionaryType, callback: (result: LpcJobResult | null) => void): void {
        emberFrameBaker.request(
            this.spriteName,
            action,
            // Wanted now, ahead of everything made ready for later.
            !this.ahead,
            baked => {
                if (!baked) {
                    callback(null)
                    this.settleFailed(action)
                    return
                }
                this.baked.set(action, baked)
                const animation = new AnimatedSprite(baked.textures)
                animation.visible = false
                animation.animationSpeed = 1000 / (baked.frameMs * TICKS_PER_SECOND)
                // A held tool's frames are bigger than the 64px body frame and centre the same
                // body: shifted back by the margin, as LPC's oversize sheets are, the body stays put.
                const result = new LpcJobResult({ animation })
                result.offsetX = -(baked.textures[0].width - BODY_FRAME) / 2
                result.offsetY = -(baked.textures[0].height - BODY_FRAME) / 2
                animation.position.set(result.offsetX, result.offsetY)
                callback(result)
            },
            () => this.spriteReleased,
            this.equipment,
        )
        // A bow shot standing ends with the bow held up, then lowered: have both ready by the time it ends.
        const parsed = parseAnimationName(action)
        if (parsed?.action === "shoot" && this.streamsShots()) {
            const on = parsed.gait ? `.${parsed.gait}` : ""
            this.prepare(`${BOW_READY_ACTION}${on}-${parsed.facing}` as AnimationDictionaryType)
            if (!parsed.gait) this.prepare(`${BOW_DOWN_ACTION}-${parsed.facing}` as AnimationDictionaryType)
            else for (const contact of [0, 1]) this.prepare(`${BOW_DOWN_ACTION}${on}.${contact}-${parsed.facing}` as AnimationDictionaryType)
        }
        // A blow struck standing settles afterwards: have the settle baked by the time the blow ends.
        if (parsed && !parsed.gait && this.settlesAttack(parsed.action)) {
            emberFrameBaker.request(this.spriteName, `${SETTLE}.${parsed.action}-${parsed.facing}`, false, () => {}, () => this.spriteReleased, this.equipment)
        }
    }

    /** A humanoid with a bow: its shots standing come in a stream, the bow kept up between them (clips.ts bowStream). */
    private streamsShots = (): boolean => !!EMBER_HUMANOIDS[this.spriteName] && aimsShots(this.equipment)

    /** A settle that could not be drawn (no plan for it): stand, rather than wait on it. */
    private settleFailed = (action: AnimationDictionaryType) => {
        if (parseAnimationName(action)?.action !== SETTLE) return
        const pending = this.pendingSettle?.name === action
        if (pending) this.pendingSettle = undefined
        if (pending || this.awaits(action)) this.activateAnimation(`idle-${this.facingDir}` as AnimationDictionaryType)
    }

    /** A blow struck standing, with this body: eased into from standing and settled out of. */
    private settlesAttack = (action: string): boolean => this.settles && STANCE_ATTACKS.includes(action) && !this.equipment.carry

    /** The blow this settle follows has played out and is waiting on it. */
    private awaits = (settle: AnimationDictionaryType): boolean => {
        const parsed = parseAnimationName(settle)
        const current = parseAnimationName(this.action)
        if (!parsed?.after || !current || current.gait || current.action !== parsed.after || current.facing !== parsed.facing) return false
        return !this.animations[this.action]?.animation?.playing
    }

    /**
     * Where the current frame puts a joint, in this sprite's own space: through the frame as it is
     * drawn now -- offset when bigger, and lifted off the ground by a jump or a flourish -- so what
     * hangs off a joint (a carried load, a halo) goes where the body goes.
     */
    private jointLocal = (pick: (joints: EmberJoints) => { x: number; y: number }): { x: number; y: number } | undefined => {
        const baked = this.baked.get(this.action)
        const animation = this.animations[this.action]?.animation
        const joints = baked?.joints[animation?.currentFrame ?? 0]
        if (!baked || !joints || !this.initialized) return undefined
        const point = pick(joints)
        if (!animation) {
            // An Ember body is placed at the origin (lpc_composite), so only a bigger frame's growth moves it.
            const grow = (baked.textures[0].width - BODY_FRAME) / 2
            return { x: point.x - grow, y: point.y - grow }
        }
        animation.transform.updateLocalTransform()
        const at = animation.localTransform.apply({
            x: point.x - animation.anchor.x * animation.texture.width,
            y: point.y - animation.anchor.y * animation.texture.height,
        })
        return { x: at.x, y: at.y }
    }

    /**
     * The feet of the frame on show, in this sprite's own space: the ground under each (where it stands
     * however high a jump has the frame), how far the sole is off it, whether it bears weight, and whether
     * the body hides it.
     */
    feetLocal(): { x: number; y: number; lift: number; down: boolean; behind: boolean }[] | undefined {
        const feet = this.baked.get(this.action)?.joints[this.animations[this.action]?.animation?.currentFrame ?? 0]?.feet
        if (!feet?.length) return undefined
        const air = this.flourishLift()
        return feet.map(foot => {
            const at = this.jointLocal(() => foot)
            return at && { x: at.x, y: at.y + air, lift: foot.lift + air, down: foot.down && air < 1, behind: foot.behind }
        }).filter(Boolean)
    }

    /** The head joint of the frame on show, in this sprite's own space. */
    headLocal = () => this.jointLocal(j => j.head)

    /** Top of the visible hair/head, in body space, following the current frame's transforms. */
    headTopLocal = () => {
        const animation = this.animations[this.action]?.animation
        const top = this.baked.get(this.action)?.headTops?.[animation?.currentFrame ?? 0]
        return top === undefined ? undefined : this.jointLocal(j => ({ x: j.head.x, y: top }))
    }

    /** Bow grip in body space; late remote shots use the release pose rather than an idle hand. */
    arrowGripLocal(angle: number): { x: number; y: number } | undefined {
        if (this.equipment.held !== "bow") return undefined
        const facing = this.calcDir(angle)
        if (parseAnimationName(this.action)?.action === "shoot" && this.facingDir === facing) {
            const grip = this.jointLocal(j => j.hands[0])
            if (grip) return grip
        }
        const plan = planEmberAnimation(this.spriteName, `shoot-${facing}`, this.equipment)
        if (!plan) return undefined
        const grip = plan.jointsAt(Math.floor(plan.frameCount / 2)).hands[0]
        // The plan centres the 64px body in its larger weapon frame, just like the baked art.
        const bodyGrowth = (EMBER_HUMANOIDS[this.spriteName].frame ?? BODY_FRAME) - BODY_FRAME
        const grow = (heldFrame("bow") + bodyGrowth - BODY_FRAME) / 2
        return { x: grip.x - grow, y: grip.y - grow }
    }

    /**
     * The skull a halo or a skeleton's effects hang from: the Ember body's own head, frame by frame,
     * where the LPC sprite reads a track measured off LPC's skeleton sheet.
     */
    headAttachmentWorld = (race: string) => {
        if (race !== "skeleton") return undefined
        const head = this.jointLocal(j => j.head)
        return head && this.toGlobal(head)
    }

    /** A skeleton's grip for what its attack holds or casts: between its raised hands in a cast, else its right fist. */
    heldAttackGripWorld = (race: string) => {
        if (race !== "skeleton") return undefined
        const casting = this.action.startsWith("cast-")
        const grip = this.jointLocal(j => (casting ? { x: (j.hands[0].x + j.hands[1].x) / 2, y: (j.hands[0].y + j.hands[1].y) / 2 } : j.hands[1]))
        if (!grip) return undefined
        const hands = casting ? [0, 1].map(i => this.toGlobal(this.jointLocal(j => j.hands[i])!)) : undefined
        const world = this.toGlobal(grip)
        return { x: world.x, y: world.y, scale: Math.hypot(this.worldTransform.a, this.worldTransform.b), hands }
    }

    calcDir(angle: number): Facing {
        return emberFacingAt(angle)
    }

    doMovement(entity: Entity): void {
        this.noteTravel(entity)
        this.aim = entity.movement?.angle
        this.runKey = entity.entityType === "player" ? entity.boosted === true : undefined
        this.player = entity.entityType === "player"
        this.settles = !!EMBER_HUMANOIDS[this.spriteName]
        this.breathless = entity.faction === "undead"
        // A player can jump at any moment: have the jump for the way it faces drawn before it does.
        if (this.player && this.settles && this.initialized && entity.movement) {
            const facing = this.calcDir(entity.movement.angle)
            if (!this.jumpsBaked.has(facing)) {
                this.jumpsBaked.add(facing)
                emberFrameBaker.request(this.spriteName, `jump-${facing}`, false, () => {}, () => this.spriteReleased, this.equipment)
            }
        }
        // Its walk and run, every way, drawn ahead in the browser's idle moments: a body whose stride for
        // the way it goes is not drawn yet glides along in its standing frame until it is.
        if (this.settles && this.initialized && !this.gaitsQueued) {
            this.gaitsQueued = true
            for (const gait of MOVING_ATTACK_GAITS) for (const facing of FACINGS) emberFrameBaker.requestIdle(this.spriteName, `${gait}-${facing}`, this.equipment)
        }
        // Setting off into a run from standing plays a start: a player has one ready for every facing.
        if (this.player && this.settles && this.initialized && !this.startsBaked && !this.equipment.carry) {
            this.startsBaked = true
            for (const gait of MOVING_ATTACK_GAITS) for (const facing of FACINGS) this.prepareAhead(`${START}.${gait}-${facing}` as AnimationDictionaryType)
        }
        // Moving again before the settle it was waiting on has baked: pick the stride back up.
        const at = entity.location
        const moved = !!at && this.lastX !== undefined && (at.x !== this.lastX || at.y !== this.lastY)
        if (this.pendingSettle && moved) this.resumeStride()
        // Moving again within an NPC's pause: its stride goes on from the frame it held.
        if (this.strideHeld && moved) {
            if (this.action === this.strideHeld.action) this.animations[this.action]?.animation?.play()
            this.strideHeld = undefined
        }
        const action = parseAnimationName(this.action)?.action ?? ""
        if ((!this.action.startsWith("idle-") && !isIdleVariation(action)) ||
            (at && this.lastX !== undefined && (at.x !== this.lastX || at.y !== this.lastY))) {
            this.nextIdleChoice = Date.now() + IDLE_CHOICE_MS
            this.pendingIdle = undefined
        }
        super.doMovement(entity)
    }

    private resumeStride = () => {
        const pending = this.pendingSettle
        this.pendingSettle = undefined
        if (pending && this.action === pending.stride) this.animations[this.action]?.animation?.play()
    }

    /**
     * Stopped: settle from the stride's frame (the next even one -- settles are drawn from every
     * other frame). Until that settle has baked the stride holds its frame, and gives up and stands
     * after SETTLE_WAIT_MS. A settle under way is left to finish: it hands over to idle itself.
     */
    protected restAnimation(): AnimationDictionaryType {
        const current = parseAnimationName(this.action)
        const idle0 = () => this.turnToward("idle", this.facingDir)
        // The bow up after a shot, ready for the next; lowered once BOW_READY_MS pass with none. Lowering plays out.
        if ((current?.action === BOW_READY_ACTION || current?.action === BOW_DOWN_ACTION) && current.after) {
            // Stopped with the bow up (or coming down) on the move: stood with it up, if that is drawn.
            const ready = `${BOW_READY_ACTION}-${current.facing}` as AnimationDictionaryType
            return current.action === BOW_READY_ACTION && this.animations[ready] ? ready : idle0()
        }
        if (current?.action === BOW_READY_ACTION) {
            const since = this.bowReadySince ?? Date.now()
            const lower = `${BOW_DOWN_ACTION}-${current.facing}` as AnimationDictionaryType
            return Date.now() - since > BOW_READY_MS && this.animations[lower] ? lower : this.action
        }
        if (current?.action === BOW_DOWN_ACTION && this.animations[this.action]?.animation?.playing) return this.action
        const idle = this.turnToward("idle", this.facingDir)
        this.holdStill()
        if (current && isIdleVariation(current.action)) return this.action
        if (this.action.startsWith("idle-") && EMBER_HUMANOIDS[this.spriteName] && Date.now() >= this.nextIdleChoice && this.atRestFrame()) {
            this.nextIdleChoice = Date.now() + IDLE_CHOICE_MS
            const choice = Math.random()
            const gesture = Math.min(IDLE_VARIATIONS.length - 1, Math.floor((choice / IDLE_GESTURE_CHANCE) * IDLE_VARIATIONS.length))
            this.pendingIdle = choice >= IDLE_GESTURE_CHANCE ? undefined : `${IDLE_VARIATIONS[gesture]}-${this.facingDir}` as AnimationDictionaryType
            return this.pendingIdle ?? idle
        }
        if (current?.action === SETTLE) return this.action
        if (!this.settles || !current || current.gait || !MOVING_ATTACK_GAITS.includes(current.action)) return idle
        const pending = this.pendingSettle
        if (pending?.stride === this.action) {
            if (Date.now() - pending.since <= SETTLE_WAIT_MS) return pending.name
            this.pendingSettle = undefined
            return idle
        }
        const stride = this.animations[this.action]?.animation
        if (!stride) return idle
        // An NPC only just stopped holds its stride a moment: if it goes again, the stride goes on.
        if (!this.player) {
            if (this.strideHeld?.action !== this.action) {
                this.strideHeld = { action: this.action, since: Date.now() }
                stride.stop()
            }
            if (Date.now() - this.strideHeld.since < NPC_STOP_HOLD_MS) return this.action
        }
        // A player settles from every other frame; an NPC from every fourth, to keep its looks' settles few.
        const every = this.player ? 2 : 4
        const frame = (every * Math.ceil(stride.currentFrame / every)) % stride.totalFrames
        const name = `${SETTLE}.${current.action}.${frame}-${current.facing}` as AnimationDictionaryType
        if (this.animations[name]) return name
        stride.stop()
        this.pendingSettle = { name, stride: this.action, since: Date.now() }
        return name
    }

    /**
     * Now and then stand quite still between breaths: at each rest frame the idle comes to, hold it
     * for a while or breathe on. Leaving the idle lets the hold go (coming back plays it afresh).
     */
    private holdStill = () => {
        const idle = parseAnimationName(this.action)?.action === "idle" ? this.animations[this.action]?.animation : undefined
        if (!idle || idle.totalFrames <= 1) {
            this.stillUntil = undefined
            this.restFrameDecided = false
            return
        }
        if (this.breathless) {
            if (idle.playing || idle.currentFrame !== 0) idle.gotoAndStop(0)
            this.stillUntil = undefined
            return
        }
        if (this.stillUntil !== undefined) {
            if (idle.playing) this.stillUntil = undefined
            else if (Date.now() >= this.stillUntil) {
                this.stillUntil = undefined
                idle.play()
            }
            return
        }
        if (!this.atRestFrame()) {
            this.restFrameDecided = false
            return
        }
        if (this.restFrameDecided) return
        this.restFrameDecided = true
        if (Math.random() >= IDLE_HOLD_CHANCE) return
        idle.stop()
        this.stillUntil = Date.now() + IDLE_HOLD_MS[0] + Math.random() * (IDLE_HOLD_MS[1] - IDLE_HOLD_MS[0])
    }

    /**
     * The breathing idle is on a frame where the body stands exactly in its rest pose (its first, or
     * half way, between the two breaths): a gesture set out from rest starts there without a jump.
     */
    private atRestFrame = (): boolean => {
        const idle = this.animations[this.action]?.animation
        if (!idle || idle.totalFrames <= 1) return true
        return idle.currentFrame === 0 || idle.currentFrame * 2 === idle.totalFrames
    }

    /** Turning while coming to rest: stand facing the new way (the settle is drawn facing the old one). */
    protected turnedAnimation(facing: string): AnimationDictionaryType {
        // Turning with the bow up between shots: up the new way if that is drawn, else stood down that way.
        const heldOn = parseAnimationName(this.action)
        if ((heldOn?.action === BOW_READY_ACTION || heldOn?.action === BOW_DOWN_ACTION) && heldOn.after) {
            // On the move: up the new way if that is drawn, else the plain stride that way.
            const ready = `${BOW_READY_ACTION}.${heldOn.after}-${facing}` as AnimationDictionaryType
            if (heldOn.action === BOW_READY_ACTION && this.animations[ready]) return ready
            this.prepareAhead(ready)
            return `${heldOn.after}-${facing}` as AnimationDictionaryType
        }
        const held = heldOn?.action
        if (held === BOW_READY_ACTION || held === BOW_DOWN_ACTION) {
            const ready = `${BOW_READY_ACTION}-${facing}` as AnimationDictionaryType
            if (held === BOW_READY_ACTION && this.animations[ready]) return ready
            this.prepareAhead(ready)
            return `idle-${facing}` as AnimationDictionaryType
        }
        if (isIdleVariation(parseAnimationName(this.action)?.action ?? "")) {
            this.pendingIdle = undefined
            this.nextIdleChoice = Date.now() + IDLE_CHOICE_MS
            return this.turnToward("idle", facing)
        }
        // Striding or standing, the body comes round through the facings between.
        const loop = parseAnimationName(this.action)
        if (loop && loopsOn(loop)) return this.turnToward(loop.action, facing)
        if (parseAnimationName(this.action)?.action === SETTLE) return `idle-${facing}` as AnimationDictionaryType
        // Turning while setting off or changing gait: straight into the stride the new way (they are drawn facing the old one).
        const parsed = parseAnimationName(this.action)
        if (parsed && handsOver(parsed)) {
            this.handingOver = undefined
            return `${strideAfter(parsed)}-${facing}` as AnimationDictionaryType
        }
        return super.turnedAnimation(facing)
    }

    /**
     * A settle starts when it lands only if it is still wanted: the stride it was asked for is still
     * holding its frame, or the blow it follows has played out. Moved, turned or struck again in the
     * meantime, it is kept for next time instead.
     */
    protected adoptsOnLoad(action: AnimationDictionaryType): boolean {
        // Starts and gait changes are made ready ahead of time and only ever played when wanted (locomotionAnimation).
        if (handsOver(parseAnimationName(action))) return false
        // The bow held up or lowered lands late: only taken up if the shot it follows has played out.
        const bow = parseAnimationName(action)?.action
        if (bow === BOW_READY_ACTION || bow === BOW_DOWN_ACTION) {
            const current = parseAnimationName(this.action)
            return current?.action === "shoot" && !this.animations[this.action]?.animation?.playing
        }
        // A step of a turn made ready for next time: not to be shown when it lands, facing the wrong way
        // -- unless the body is showing nothing at all yet. Then whatever stand or stride lands first
        // is taken, the wrong way or not, and the body turns from there: a body whose facing keeps
        // moving while its first frames bake (an NPC turning to face someone) would otherwise refuse
        // every one of them and never be seen.
        const loop = parseAnimationName(action)
        if (loop && loopsOn(loop) && (loop.toward || loop.facing !== this.facingDir)) return !this.animations[this.action]
        if (isIdleVariation(parseAnimationName(action)?.action ?? "")) return action === this.pendingIdle && this.action.startsWith("idle-") && parseAnimationName(action)?.facing === this.facingDir
        if (THREATEN_ACTIONS.includes(parseAnimationName(action)?.action ?? "")) return this.attackIntentPose?.action === action
        if (parseAnimationName(action)?.action !== SETTLE) return true
        const pending = this.pendingSettle
        if (pending?.name === action && this.action === pending.stride) {
            this.pendingSettle = undefined
            return true
        }
        return this.awaits(action)
    }

    /** Standing (or as good as): a blow from here is struck from standing. */
    private standing = (): boolean => {
        if (parseAnimationName(this.action)?.action === "idle" || isIdleVariation(parseAnimationName(this.action)?.action ?? "") || this.pendingSettle?.stride === this.action) return true
        const parsed = parseAnimationName(this.action)
        if (parsed?.action !== SETTLE) return false
        // Barely out of a blow, the stance is still there to strike again from.
        const settle = this.animations[this.action]?.animation
        return !STANCE_ATTACKS.includes(parsed.after!) || !settle || settle.currentFrame * 2 >= settle.totalFrames
    }

    /**
     * In the air in a jump (game-common/jump.ts): the clip's frame for how far through it the body is,
     * in step with the lift. A leap off an edge holds the legs-reaching-down frame until its drop has
     * landed, and then takes the landing in the knees. A strike thrown in the air plays over it.
     */
    protected jumpPose(entity: Entity): BodyAnimationPose | undefined {
        if (!EMBER_HUMANOIDS[this.spriteName]) return undefined
        const now = Date.now()
        const fall = entity.floorFall
        // Or still in the storey it leapt from, already dropping: this client starts the drop itself.
        const pending = !fall ? predictedFallPending(entity, now) : undefined
        const predicted = pending?.view.leap && predictedFallAirborne(pending, now) ? pending : undefined
        // A drop on the shared curve has come down when this client's clock says so: the server's "landed" is a
        // round trip behind it, and the body waited out that trip on the ground in the legs-reaching-down frame.
        const leaping = !!predicted || (!!fall?.leap && fall.phase !== "landed" && (!fall.trajectory || isAirborne(entity)))
        if (leaping) {
            this.leapFallId = fall?.id ?? predicted.view.id
            this.leapLandedAt = undefined
        } else if (this.leapFallId !== undefined) {
            this.leapFallId = undefined
            this.leapLandedAt = now
        }
        let progress = this.flourishProgress(JUMP_FLOURISH)
        if (leaping) progress = JUMP_HOLD
        else if (this.leapLandedAt !== undefined) {
            const landing = JUMP_LANDING + (now - this.leapLandedAt) / JUMP_MS
            if (landing < 1) progress = landing
            else this.leapLandedAt = undefined
        }
        if (progress === undefined) {
            if (isLeap(parseAnimationName(this.jumpAction ?? ""))) this.landedLeap = { leap: this.jumpAction!, at: now }
            this.jumpFacing = undefined
            this.jumpAction = undefined
            return undefined
        }
        if (this.striking()) return undefined
        this.jumpFacing ??= this.calcDir(entity.movement?.angle ?? this.spriteAngle)
        this.jumpAction ??= this.leapFrom(this.jumpFacing) ?? (`jump-${this.jumpFacing}` as AnimationDictionaryType)
        return { action: this.jumpAction, progress }
    }

    /**
     * Jumping on the move: a leap out of the stride on show, from the quarter of it nearest where the
     * legs are, if that leap is ready (made ready now for next time if not). Standing, or in anything
     * but a stride, undefined: the standing jump.
     */
    private leapFrom = (facing: Facing): AnimationDictionaryType | undefined => {
        const current = parseAnimationName(this.action)
        const legs = this.animations[this.action]?.animation
        if (!this.player || !this.settles || this.equipment.carry || this.backward || !current || !legs || !loopsOn(current) || current.action === "idle") return undefined
        const quarter = Math.round((legs.currentFrame / legs.totalFrames) * LEAP_PHASES) % LEAP_PHASES
        const leap = `jump.${current.action}.${quarter}-${facing}` as AnimationDictionaryType
        if (this.animations[leap]) return leap
        this.prepareLeaps(current.action, facing)
        return undefined
    }

    /** The leaps out of a stride in a facing, made ready the first time the body runs (or jumps walking) that way. */
    private prepareLeaps = (stride: string, facing: string) => {
        const key = `${stride}-${facing}`
        if (this.leapsPrepared.has(key) || !this.initialized) return
        this.leapsPrepared.add(key)
        for (let quarter = 0; quarter < LEAP_PHASES; quarter++) this.prepareAhead(`jump.${stride}.${quarter}-${facing}` as AnimationDictionaryType)
    }

    /** A generated humanoid lies down as LPC does, and falls with its arms flung up (clips.ts `falling`). */
    protected drawsHeldPose(pose: HeldPose): boolean {
        return pose === "downed" || !!EMBER_HUMANOIDS[this.spriteName]
    }

    /**
     * Drive the full climb cycle by distance along the ladder. Holding a rung
     * freezes the pose; descending or reversing retraces the same frames.
     */
    protected climbPose(entity: Entity): BodyAnimationPose | undefined {
        const rise = ladderRiseOf(entity)
        if (rise === undefined) return undefined
        const frames = EMBER_CLIPS.climb.frames
        const cycle = (((rise / CLIMB_RISE) % 1) + 1) % 1
        return { action: "climb-back", progress: cycle, frame: Math.floor(cycle * frames) % frames }
    }

    protected locomotionAnimation(): AnimationDictionaryType {
        const speed = this.walkPace.speed
        if (this.runKey !== undefined) this.running = this.runKey
        else if (this.running && speed > 0 && speed < WALK_BELOW) this.running = false
        else if (!this.running && speed > RUN_ABOVE) this.running = true
        const stride = this.running ? "run" : "walk"
        const current = parseAnimationName(this.action)
        const eases = this.settles && !this.equipment.carry
        // On the move with the bow up between shots: kept up while the stride goes on the same way;
        // after BOW_READY_MS, lowered into the stride at its next heel strike. Changing gait or way,
        // the plain stride (on from where the legs were).
        if ((current?.action === BOW_READY_ACTION || current?.action === BOW_DOWN_ACTION) && current.after) {
            const same = current.after === stride && current.facing === this.facingDir
            if (same && current.action === BOW_DOWN_ACTION) return this.action
            if (same) {
                const since = this.bowReadySince ?? Date.now()
                if (Date.now() - since <= BOW_READY_MS) return this.action
                const legs = this.animations[this.action]?.animation
                const contact = !legs ? undefined : legs.currentFrame === 0 ? 0 : legs.currentFrame * 2 === legs.totalFrames ? 1 : undefined
                const lower = contact !== undefined && (`${BOW_DOWN_ACTION}.${stride}.${contact}-${this.facingDir}` as AnimationDictionaryType)
                // (Backwards, the lowering's forward stride would not fit: straight to the stride, reversed.)
                if (lower && this.animations[lower] && !this.backward) {
                    this.handingOver = lower
                    return lower
                }
                if (Date.now() - since <= BOW_READY_MS + SHIFT_WAIT_MS) return this.action
            }
            return `${stride}-${this.facingDir}` as AnimationDictionaryType
        }
        // Just down from a leap: the stride it leapt out of goes on from where the leap left the legs.
        const landed = this.landedLeap
        this.landedLeap = undefined
        if (landed && Date.now() - landed.at < LEAP_LANDING_MS) {
            const leap = parseAnimationName(landed.leap)
            if (leap && leap.gait === stride && leap.facing === this.facingDir && !handsOver(current)) {
                this.handingOver = landed.leap
                return `${stride}-${this.facingDir}` as AnimationDictionaryType
            }
        }
        const gait = this.shortened(stride, this.turnToward(stride, this.facingDir))
        if (eases && this.player) this.prepareTurns(stride, this.facingDir)
        if (eases) this.prepareShifts(this.facingDir)
        if (eases && this.player && stride === "run") this.prepareLeaps(stride, this.facingDir)
        // Walk to run or back mid-stride (out of stamina, say): carry on to the next heel strike and
        // change there through a shift (settle.ts shiftStride); without one, the new gait goes on
        // from where the legs were.
        if (current && current.action !== stride && MOVING_ATTACK_GAITS.includes(current.action) && !current.gait) {
            const legs = this.animations[this.action]?.animation
            // (Backwards, a shift's forward blend would not fit: straight across, the legs kept where they were.)
            if (legs && eases && !this.backward) {
                this.shiftWanted ??= Date.now()
                const contact = legs.currentFrame === 0 ? 0 : legs.currentFrame * 2 === legs.totalFrames ? 1 : undefined
                const shift = contact !== undefined && (`${SHIFT}.${current.action}.${contact}-${current.facing}` as AnimationDictionaryType)
                if (shift && this.animations[shift]) {
                    this.shiftWanted = undefined
                    this.handingOver = shift
                    return shift
                }
                if (Date.now() - this.shiftWanted < SHIFT_WAIT_MS) return this.action
            }
            this.shiftWanted = undefined
            return gait
        }
        this.shiftWanted = undefined
        // A gait change plays out while the body keeps to the gait it is changing to.
        if (current?.action === SHIFT && current.facing === this.facingDir && strideAfter(current) === stride) return this.action
        if (!eases) return gait
        // Setting off from standing -- or from the bow held up between shots -- the start, while it
        // plays the way the body is going. Set off walking a moment before the run key's word
        // arrived, it sets off running instead.
        const fromBow = current?.action === BOW_READY_ACTION || (current?.action === START && !!current.fromBow)
        const start = `${START}.${stride}${fromBow ? `.${BOW_READY_ACTION}` : ""}-${this.facingDir}` as AnimationDictionaryType
        if (this.action === start) return start
        const standing = current?.action === "idle" || current?.action === BOW_READY_ACTION || (!!current && isIdleVariation(current.action))
        // Not made yet (an NPC's, or one from the bow): made now for next time.
        if (standing && !this.animations[start]) this.prepareAhead(start)
        const justSetOff = current?.action === START && current.facing === this.facingDir && Date.now() - this.setOffAt < START_SWAP_MS
        if (!(standing || justSetOff) || !this.animations[start] || this.backward) return gait
        // Too slow for a start to carry the body off in good time: straight into the stride.
        const startBaked = this.baked.get(start)
        const pace = this.walkPace.speed
        if (startBaked?.strideCycle && pace > 0) {
            const framesPerSecond = gaitAnimationSpeed(pace, startBaked.strideCycle, startBaked.textures.length) * TICKS_PER_SECOND
            if ((startBaked.textures.length / framesPerSecond) * 1000 > START_MAX_MS) return gait
        }
        if (standing) this.setOffAt = Date.now()
        this.handingOver = start
        return start
    }

    /**
     * The walk drawn at the stride the body's pace calls for: going slowly, a shorter one (clips.ts
     * shortWalk) rather than the full stride in slow motion. Only for the walk the way it faces (not a
     * step of a turn), and once that walk is made -- until then the full one, the shorter made for next time.
     */
    private shortened = (stride: string, gait: AnimationDictionaryType): AnimationDictionaryType => {
        if (stride !== "walk" || !this.settles || gait !== `walk-${this.facingDir}`) return gait
        const speed = this.walkPace.speed
        if (speed > 0) {
            const tierAt = (v: number) => {
                let tier = 0
                while (tier < SHORT_STRIDE_BELOW.length && v < SHORT_STRIDE_BELOW[tier]) tier++
                return tier
            }
            // Slower by the margin to take a shorter stride; faster by it to take a longer one back.
            const slower = tierAt(speed + STRIDE_MARGIN), faster = tierAt(speed - STRIDE_MARGIN)
            if (slower > this.strideTier) this.strideTier = slower
            else if (faster < this.strideTier) this.strideTier = faster
        }
        if (!this.strideTier) return gait
        const short = `walk.${SHORT_STRIDE}${Math.round(SHORT_STRIDES[this.strideTier - 1] * 100)}-${this.facingDir}` as AnimationDictionaryType
        if (this.animations[short]) return short
        this.prepareAhead(short)
        return gait
    }

    /** The half way turns either side of a facing, made ready the first time the body walks or runs that way. */
    private prepareTurns = (stride: string, facing: string) => {
        const key = `${stride}-${facing}`
        const at = SCREEN_RING.indexOf(facing as Facing) * 2
        if (this.turnsPrepared.has(key) || at < 0 || !this.initialized) return
        this.turnsPrepared.add(key)
        for (const side of [1, 15]) this.prepareAhead(ringDrawing(stride, (at + side) % 16))
    }

    /** Gait changes for a facing, made ready the first time the body walks or runs that way. */
    private prepareShifts = (facing: string) => {
        if (this.shiftsPrepared.has(facing) || !this.initialized) return
        this.shiftsPrepared.add(facing)
        for (const gait of MOVING_ATTACK_GAITS) for (const contact of [0, 1]) this.prepareAhead(`${SHIFT}.${gait}.${contact}-${facing}` as AnimationDictionaryType)
    }

    /**
     * Aim the next shot along screen heading `heading` (radians, clockwise from screen right): from
     * the bow to where the arrow will land, so the arrow drawn on the bow points the way it flies.
     * Undefined draws the shot along the facing, as it always was.
     */
    aimShot(heading: number | undefined) {
        this.shotHeading = heading
    }

    /** `action`, a shot, drawn along the heading it was aimed at, for a body that aims its shots. */
    private aimed = (action: AnimationDictionaryType): AnimationDictionaryType =>
        this.shotHeading !== undefined && this.attackAction === "shoot" && aimsShots(this.equipment)
            ? (aimedShotName(action, this.shotHeading) as AnimationDictionaryType)
            : action

    protected attackAnimation(): AnimationDictionaryType {
        // Another shot with the bow still up from the last: from there, not from rest.
        const standingReady = parseAnimationName(this.action)
        if (this.attackAction === "shoot" && standingReady?.action === BOW_READY_ACTION && !standingReady.after) {
            this.resumeGait = undefined
            return this.aimed(`shoot.${AGAIN}-${this.facingDir}` as AnimationDictionaryType)
        }
        const shown = parseAnimationName(this.action)
        // On the move with the bow up: the stride under it is the one to shoot over.
        const readyOn = shown?.action === BOW_READY_ACTION && shown.after ? shown.after : undefined
        const gait = readyOn ?? this.action.split("-")[0]
        const stride = this.animations[this.action]?.animation
        this.resumeGait = undefined
        if (this.settlesAttack(this.attackAction) && this.standing()) {
            this.pendingSettle = undefined
            return `${this.attackAction}.${FROM_REST}-${this.facingDir}` as AnimationDictionaryType
        }
        if (!stride || !MOVING_ATTACK_GAITS.includes(gait) || !(this.attackAction in LPC_ACTION_DURATION_MS)) {
            return this.aimed(super.attackAnimation())
        }
        // Walking or running: strike over the legs from the point of the stride they are at, the
        // legs keeping the pace the body is actually crossing the ground at.
        const phase = stride.currentFrame / stride.totalFrames
        const speed = this.walkPace.speed > 0 ? this.walkPace.speed : undefined
        // A bow shot on the move ends with the bow up, the stride going on under it (clips.ts bowStream);
        // one from the bow already up starts there.
        const streams = this.attackAction === "shoot" && this.streamsShots()
        // Going backwards to the way it faces, the stride under the blow runs backwards too.
        const name = movingAttackName(this.attackAction, gait, phase, this.facingDir as Facing, speed, this.backward)
        const strike = this.aimed((readyOn && streams ? name.replace(/^shoot\./, `shoot.${AGAIN}.`) : name) as AnimationDictionaryType)
        this.resumeGait = { action: `${streams ? `${BOW_READY_ACTION}.` : ""}${gait}-${this.facingDir}` as AnimationDictionaryType, after: strike }
        return strike
    }

    /**
     * After a strike on the move, straight back into the stride; after a blow struck standing, its
     * settle (for a body that settles); after a settle, idle. Anything else goes on as LPC's does.
     */
    postAnimation = (animation: AnimationDictionaryType): AnimationDictionaryType => {
        const parsed = parseAnimationName(animation)
        if (parsed && isIdleVariation(parsed.action)) return `idle-${parsed.facing}` as AnimationDictionaryType
        // A bow shot standing keeps the bow up for the next; lowered, the archer stands.
        if (parsed?.action === "shoot" && this.streamsShots())
            return `${BOW_READY_ACTION}${parsed.gait ? `.${parsed.gait}` : ""}-${parsed.facing}` as AnimationDictionaryType
        if (parsed?.action === BOW_DOWN_ACTION) return `idle-${parsed.facing}` as AnimationDictionaryType
        // Into the stride, from where the start or gait change left it (animationStartFrame).
        if (parsed && handsOver(parsed)) return `${strideAfter(parsed)}-${parsed.facing}` as AnimationDictionaryType
        if (parsed?.gait) return `${parsed.gait}-${parsed.facing}` as AnimationDictionaryType
        if (parsed?.action === SETTLE) return `idle-${parsed.facing}` as AnimationDictionaryType
        if (parsed && this.settlesAttack(parsed.action)) return `${SETTLE}.${parsed.action}-${parsed.facing}` as AnimationDictionaryType
        return this.standingPostAnimation(animation)
    }

    protected animationStartFrame(action: AnimationDictionaryType, animation?: AnimatedSprite): number {
        if (handsOver(parseAnimationName(action))) return super.animationStartFrame(action, animation)
        // A stride or standing loop taking over from another (a new facing, the other gait): on from
        // where that one had got to. Both gaits count their cycle from the left heel's strike.
        const leaving = this.leaving
        this.leaving = undefined
        const was = leaving && parseAnimationName(leaving.action)
        const now = parseAnimationName(action)
        if (was && animation && loopsOn(now) && (was.action === now!.action || (was.action !== "idle" && now!.action !== "idle"))) {
            this.handingOver = undefined
            return Math.round(leaving!.phase * animation.totalFrames) % animation.totalFrames
        }
        // The stride a start or gait change hands over to: on from where it left the legs.
        const from = this.handingOver
        this.handingOver = undefined
        const handover = from && parseAnimationName(from)
        if (handover && handsOver(handover) && animation && action === `${strideAfter(handover)}-${handover.facing}`) {
            const after = this.baked.get(from)?.gaitPhaseAfter
            if (after !== undefined) return Math.round(after * animation.totalFrames) % animation.totalFrames
        }
        // Picking the stride back up after a strike on the move: on from where the strike left the legs.
        const resume = this.resumeGait
        if (resume && animation && action === resume.action) {
            const after = this.baked.get(resume.after)?.gaitPhaseAfter
            this.resumeGait = undefined
            if (after !== undefined) return Math.round(after * animation.totalFrames) % animation.totalFrames
        }
        return super.animationStartFrame(action, animation)
    }

    protected applyPace(action: AnimationDictionaryType, animation?: AnimatedSprite): void {
        super.applyPace(action, animation)
        if (animation && isIdleVariation(parseAnimationName(action)?.action ?? "")) animation.loop = false
        const baked = this.baked.get(action)
        const speed = this.walkPace.speed
        if (!animation || !baked?.strideCycle || speed <= 0) return
        // A plain stride walked backwards plays in reverse (see BACKWARD_ON).
        const parsed = parseAnimationName(action)
        // (So does the stride under a bow held up on the move.)
        const reversed = this.backward && !!parsed && !parsed.gait && (MOVING_ATTACK_GAITS.includes(parsed.action) || (parsed.action === BOW_READY_ACTION && !!parsed.after))
        animation.animationSpeed = gaitAnimationSpeed(speed, baked.strideCycle, baked.textures.length) * (reversed ? -1 : 1)
    }

    /**
     * Which way the body is drawn going, against the way it faces: a smoothed heading of its moves on
     * screen, so a single nudge does not swing it; going backwards past BACKWARD_ON, forwards again
     * inside BACKWARD_OFF. A body that has stood still a while keeps going forwards.
     */
    private noteTravel = (entity: Entity) => {
        const at = entity.location
        const now = Date.now()
        if (!at || this.lastX === undefined || this.lastY === undefined) return
        const dx = at.x - this.lastX, dy = at.y - this.lastY
        if (!dx && !dy) {
            if (now - this.travel.at > 400) this.backward = false
            return
        }
        const dt = Math.max(1, now - this.travel.at)
        this.travel = { x: this.travel.x * (1 - TRAVEL_SMOOTHING) + dx * TRAVEL_SMOOTHING, y: this.travel.y * (1 - TRAVEL_SMOOTHING) + dy * TRAVEL_SMOOTHING, at: now }
        const length = Math.hypot(this.travel.x, this.travel.y)
        if (length <= 0 || (Math.hypot(dx, dy) * 1000) / dt < TRAVEL_MIN) return
        const facing = facingScreenAngle(this.facingDir as Facing)
        const along = (this.travel.x * Math.cos(facing) + this.travel.y * Math.sin(facing)) / length
        if (!this.backward && along < BACKWARD_ON) this.backward = true
        else if (this.backward && along > BACKWARD_OFF) this.backward = false
    }
}
